import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ops, asList, fmtDate, OPS } from "@/admin/api";
import { PageTitle } from "@/admin/ui";

const FILTERS = [
  { key: "all", label: "All Users" },
  { key: "convoy", label: "Active Convoy Members" },
  { key: "free", label: "Free Riders" },
  { key: "premium", label: "Premium Riders" },
];

const dot = (color, pulse = false) =>
  L.divIcon({
    className: "",
    html: `<span class="ops-dot${pulse ? " ops-dot-pulse" : ""}" style="--dot:${color}"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const CARTO_TILES = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

const FlyTo = ({ target }) => {  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 14, { duration: 1.2 });
  }, [target, map]);
  return null;
};

const memberPos = (m) =>
  m.latitude != null && m.longitude != null ? [m.latitude, m.longitude] : null;

export default function LiveMap() {
  const [filter, setFilter] = useState("all");
  const [tiers, setTiers] = useState({});
  const [convoys, setConvoys] = useState([]);
  const [riders, setRiders] = useState([]);
  const [sos, setSos] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const lastSosId = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await ops(OPS.users);
        if (!alive) return;
        const map = {};
        asList(d, "users").forEach((u) => {
          map[u.uid || u.id] = u.tier || (u.premium ? "premium" : "free");
        });
        setTiers(map);
      } catch {}

      try {
        const d = await ops(OPS.convoys);
        const list = asList(d, "convoys");
        if (!alive) return;
        setConvoys(list);
        const located = [];
        await Promise.all(
          list.slice(0, 10).map(async (c) => {
            try {
              const detail = await ops(OPS.convoyDetail(c.id || c.convoyId));
              const members = detail?.members;
              const arr = members
                ? Array.isArray(members)
                  ? members
                  : Object.values(members)
                : [];
              arr.forEach((m) => {
                if (memberPos(m)) {
                  located.push({
                    ...m,
                    convoyId: c.id || c.convoyId,
                    convoyName: c.name || c.code,
                    convoyCode: c.code,
                  });
                }
              });
            } catch {}
          }),
        );
        if (alive) setRiders(located);
      } catch {}

      try {
        const d = await ops(OPS.sos);
        const alerts = asList(d, "alerts", "sos", "events").filter((a) => !a.resolved);
        if (!alive) return;
        setSos(alerts);
        const latest = alerts[alerts.length - 1];
        const id = latest?.id || latest?.createdAt || latest?.timestamp;
        if (latest && id && id !== lastSosId.current) {
          const had = lastSosId.current !== null;
          lastSosId.current = id;
          const loc = latest.location || latest;
          const lat = loc?.lat ?? loc?.latitude;
          const lng = loc?.lng ?? loc?.longitude;
          if (had && lat != null && lng != null) setFlyTarget([lat, lng]);
        }
      } catch {}

      if (alive) setLastRefresh(new Date());
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const visibleRiders = riders.filter((r) => {
    const tier = tiers[r.userId || r.uid || r.id] || "free";
    if (filter === "convoy") return true;
    if (filter === "free") return tier !== "premium";
    if (filter === "premium") return tier === "premium";
    return true;
  });

  return (
    <div className="-mx-4 -my-8 sm:-mx-8" data-testid="admin-livemap-page">
      <div className="px-4 pt-8 sm:px-8">
        <PageTitle eyebrow="Admin Portal" title="Live Ops Map">
          <div className="flex flex-wrap gap-2" data-testid="map-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                  filter === f.key ? "border-[#FF3333]/50 bg-[#FF3333]/15 text-white" : "border-white/15 text-neutral-400 hover:text-white"
                }`}
                data-testid={`map-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </PageTitle>
        <p className="mb-3 flex flex-wrap items-center gap-4 text-xs text-neutral-500">
          <span className="flex items-center gap-1.5"><span className="ops-dot" style={{ "--dot": "#00F0FF" }} /> Free rider</span>
          <span className="flex items-center gap-1.5"><span className="ops-dot" style={{ "--dot": "#fbbf24" }} /> Premium rider</span>
          <span className="flex items-center gap-1.5"><span className="ops-dot" style={{ "--dot": "#ffffff" }} /> Convoy destination</span>
          <span className="flex items-center gap-1.5"><span className="ops-dot" style={{ "--dot": "#FF3333" }} /> SOS alert</span>
          <span data-testid="map-last-refresh">
            {riders.length} rider{riders.length === 1 ? "" : "s"} located ·{" "}
            {lastRefresh ? `updated ${lastRefresh.toLocaleTimeString()}` : "refreshing…"} · every 15s
          </span>
        </p>
      </div>
      <div className="h-[calc(100vh-280px)] min-h-[420px] border-y border-white/10">
        <MapContainer center={[3.139, 101.6869]} zoom={11} className="h-full w-full" data-testid="live-map">
          <TileLayer
            url={CARTO_TILES}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>'
          />
          <FlyTo target={flyTarget} />
          {visibleRiders.map((r, i) => {
            const p = memberPos(r);
            if (!p) return null;
            const id = r.userId || r.uid || r.id || i;
            const tier = tiers[id] || "free";
            return (
              <Marker key={id} position={p} icon={dot(tier === "premium" ? "#fbbf24" : "#00F0FF")}>
                <Popup>
                  <strong>{r.displayName || "Rider"}</strong>
                  <br />
                  {tier === "premium" ? "Premium" : "Free"} · Convoy {r.convoyCode || r.convoyName || ""}
                  <br />
                  Last update: {fmtDate(r.lastUpdate)}
                  <br />
                  Speed: {Math.round((r.currentSpeedMps || 0) * 3.6)} km/h
                </Popup>
              </Marker>
            );
          })}
          {convoys.map((c, i) => {
            const id = c.id || c.convoyId || i;
            const dest = c.destination;
            const destPos =
              dest && (dest.latitude != null || dest.lat != null)
                ? [dest.latitude ?? dest.lat, dest.longitude ?? dest.lng]
                : null;
            if (!destPos) return null;
            return (
              <Marker key={id} position={destPos} icon={dot("#ffffff")}>
                <Popup>
                  <strong>{c.name || c.code || "Convoy"}</strong>
                  <br />
                  Code: {c.code || "—"}
                  <br />
                  Members: {c.memberCount ?? "—"} · Leader: {c.leaderId ? `${String(c.leaderId).slice(0, 8)}…` : "—"}
                  <br />
                  Started: {fmtDate(c.tripStartedAt || c.createdAt)}
                  <br />
                  Destination: {dest.address || "—"}
                </Popup>
              </Marker>
            );
          })}
          {sos.map((a, i) => {
            const loc = a.location || a;
            const lat = loc?.lat ?? loc?.latitude;
            const lng = loc?.lng ?? loc?.longitude;
            if (lat == null || lng == null) return null;
            return (
              <Marker key={a.id || i} position={[lat, lng]} icon={dot("#FF3333", true)}>
                <Popup>
                  <strong>SOS — {a.userName || a.displayName || a.userId || "Unknown rider"}</strong>
                  <br />
                  {fmtDate(a.createdAt || a.timestamp)}
                  <br />
                  {lat}, {lng}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
